import { useMemo, useState } from 'react';
import { Loader2, Plus, Building2, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useCurrentClient } from '@/hooks/useCurrentClient';
import { useAgreements } from './useAgreements';
import { useAgreementPayments } from './useAgreementPayments';
import { AgreementModal } from './AgreementModal';
import { AgreementCard } from './AgreementCard';

function formatBrl(n: number): string {
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export function AgreementsPage() {
  const { clientId, client } = useCurrentClient();
  const { data: agreements = [], isLoading: loadingAgr } = useAgreements();
  const { data: payments = [], isLoading: loadingPay } = useAgreementPayments();
  const [createOpen, setCreateOpen] = useState(false);
  const [search, setSearch] = useState('');

  const totals = useMemo(() => {
    const totalGross = payments.reduce((s, p) => s + Number(p.gross_amount), 0);
    const totalGlosa = payments.reduce((s, p) => s + Number(p.glosa_amount), 0);
    const totalNet = payments.reduce((s, p) => s + Number(p.net_amount), 0);
    return { totalGross, totalGlosa, totalNet };
  }, [payments]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return agreements;
    return agreements.filter(
      (a) =>
        a.name.toLowerCase().includes(term) ||
        a.operator?.toLowerCase().includes(term) ||
        a.contact_name?.toLowerCase().includes(term),
    );
  }, [agreements, search]);

  if (!clientId) {
    return (
      <div>
        <h1 className="mb-1 font-brand text-2xl font-bold text-foreground">Convênios</h1>
        <Card>
          <CardContent className="p-12 text-center text-muted-foreground">
            <Building2 className="mx-auto mb-3 h-10 w-10 opacity-40" />
            <p className="text-sm">Selecione um cliente no topo para gerenciar convênios.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isLoading = loadingAgr || loadingPay;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-brand text-2xl font-bold text-foreground">Convênios</h1>
          <p className="text-sm text-muted-foreground">
            {client?.name ? `${client.name} · ` : ''}repasses recebidos por convênio
          </p>
        </div>
        <Button
          onClick={() => setCreateOpen(true)}
          className="bg-primary font-medium text-primary-foreground hover:bg-primary/90"
        >
          <Plus size={16} className="mr-2" />
          Novo convênio
        </Button>
      </div>

      {/* Resumo geral */}
      {payments.length > 0 && (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <Card>
            <CardContent className="p-4">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Bruto recebido
              </p>
              <p className="mt-1 text-2xl font-bold tabular-nums text-foreground">
                {formatBrl(totals.totalGross)}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Glosa total
              </p>
              <p className="mt-1 text-2xl font-bold tabular-nums text-expense">
                {formatBrl(totals.totalGlosa)}
              </p>
              {totals.totalGross > 0 && (
                <p className="text-xs text-muted-foreground">
                  {((totals.totalGlosa / totals.totalGross) * 100).toFixed(1).replace('.', ',')}% do
                  bruto
                </p>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Líquido recebido
              </p>
              <p className="mt-1 text-2xl font-bold tabular-nums text-income">
                {formatBrl(totals.totalNet)}
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Busca */}
      {agreements.length > 3 && (
        <Card>
          <CardContent className="p-3">
            <div className="relative">
              <Search
                size={14}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
              />
              <Input
                placeholder="Buscar por nome, operadora ou contato…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Lista */}
      {isLoading ? (
        <Card>
          <CardContent className="flex items-center justify-center p-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </CardContent>
        </Card>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center text-muted-foreground">
            <Building2 className="mx-auto mb-3 h-10 w-10 opacity-40" />
            <p className="text-sm">
              {search
                ? 'Nenhum convênio corresponde à busca.'
                : 'Nenhum convênio cadastrado ainda.'}
            </p>
            {!search && (
              <Button
                variant="outline"
                size="sm"
                className="mt-4"
                onClick={() => setCreateOpen(true)}
              >
                <Plus size={14} className="mr-1.5" />
                Cadastrar primeiro convênio
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {filtered.map((a) => (
            <AgreementCard key={a.id} agreement={a} payments={payments} />
          ))}
        </div>
      )}

      <AgreementModal open={createOpen} onOpenChange={setCreateOpen} />
    </div>
  );
}
